/**
 * Permission Checker Utility
 * 
 * This utility provides helper functions to check user permissions and roles
 * throughout the application.
 */

import store from '@/store';

/**
 * Check if the current user has a specific permission or is an admin
 * @param {string} permission - The permission string to check for
 * @returns {boolean} - True if user has permission or is admin, false otherwise
 */
export const hasPermission = (permission) => {
  const user = store.state.user;
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (!permission) return true;
  return user.permissions && user.permissions.includes(permission);
};

/**
 * Check if the current user has any of the specified permissions or is an admin
 * @param {string[]} permissions - Array of permission strings to check for
 * @returns {boolean} - True if user has any of the permissions or is admin
 */
export const hasAnyPermission = (permissions) => {
  const user = store.state.user;
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (!permissions || permissions.length === 0) return true;
  return permissions.some(permission => 
    user.permissions && user.permissions.includes(permission)
  );
};

/**
 * Check if the current user has all of the specified permissions or is an admin
 * @param {string[]} permissions - Array of permission strings to check for
 * @returns {boolean} - True if user has all permissions or is admin
 */
export const hasAllPermissions = (permissions) => {
  const user = store.state.user;
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (!permissions || permissions.length === 0) return true;
  return permissions.every(permission => 
    user.permissions && user.permissions.includes(permission)
  );
};

/**
 * Check if the current user is an admin
 * @returns {boolean} - True if user is admin, false otherwise
 */
export const isAdmin = () => {
  const user = store.state.user;
  return user && user.role === 'admin';
};

/**
 * Check if the current user has a specific role
 * @param {string} role - The role to check for
 * @returns {boolean} - True if user has the specified role
 */
export const hasRole = (role) => {
  const user = store.state.user;
  return user && user.role === role;
};

/**
 * Get the current user's permissions array
 * @returns {string[]} - Array of permission strings
 */
export const getUserPermissions = () => {
  const user = store.state.user;
  return user && user.permissions ? user.permissions : [];
};

/**
 * Get the current user's role
 * @returns {string|null} - The user's role or null if no user
 */
export const getUserRole = () => {
  const user = store.state.user;
  return user ? user.role : null;
};

// Export all functions as a default object for easier importing
export default {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  isAdmin,
  hasRole,
  getUserPermissions,
  getUserRole
};
